import { useState } from 'react';
import { Zap, Pause, Play, XCircle, Loader2, CheckCircle2, ChevronDown, ChevronRight, Mail, Clock, GitBranch, AlertCircle } from 'lucide-react';
import { api, fmtDate } from './api';

const STATUS_STYLE = {
  active:    { label: 'Aktiv',         cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  running:   { label: 'Läuft',         cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  paused:    { label: 'Pausiert',      cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  completed: { label: 'Abgeschlossen', cls: 'text-[var(--text-tertiary)] bg-[var(--bg-inset)] border-[var(--border-card)]' },
  stopped:   { label: 'Beendet',       cls: 'text-red-700 bg-red-50 border-red-200' },
  cancelled: { label: 'Abgebrochen',   cls: 'text-red-700 bg-red-50 border-red-200' },
  failed:    { label: 'Fehler',        cls: 'text-red-700 bg-red-50 border-red-200' },
  error:     { label: 'Fehler',        cls: 'text-red-700 bg-red-50 border-red-200' },
};

const STEP_TYPE_ICON = {
  email: Mail,
  delay: Clock,
  branch: GitBranch,
  condition: GitBranch,
};

// #1083 Phase 4/D — Step-Status aus step_results + current_step_path ableiten
function deriveStepStatus(step, idx, exec) {
  const path = String(step.id || idx);
  const results = Array.isArray(exec.step_results) ? exec.step_results : [];
  const result = results.find((r) => r && (r.path === path || r.path === String(idx)));
  if (result) {
    if (result.success === false) return { state: 'error', result };
    return { state: 'done', result };
  }
  const isCurrent = exec.current_step_path === path || exec.current_step_path === String(idx);
  if (isCurrent && (exec.status === 'running' || exec.status === 'paused')) {
    return { state: exec.status === 'paused' ? 'paused' : 'current', result: null };
  }
  return { state: 'pending', result: null };
}

function StepRow({ step, idx, exec }) {
  const { state, result } = deriveStepStatus(step, idx, exec);
  const Icon = STEP_TYPE_ICON[step.type] || GitBranch;
  const stateStyle = {
    done:    { dot: 'bg-emerald-500',  text: 'text-[var(--text-secondary)]', label: 'Erledigt' },
    current: { dot: 'bg-[#338edc] animate-pulse', text: 'text-[var(--text-primary)] font-medium', label: 'Aktuell' },
    paused:  { dot: 'bg-amber-500',    text: 'text-[var(--text-primary)] font-medium', label: 'Pausiert' },
    error:   { dot: 'bg-red-500',      text: 'text-red-700 font-medium', label: 'Fehler' },
    pending: { dot: 'bg-[var(--border-card)]', text: 'text-[var(--text-tertiary)]', label: 'Offen' },
  }[state];

  const stepLabel = step.config?.custom_subject || step.config?.subject || step.type;

  return (
    <div className="flex items-start gap-2 py-1.5">
      <div className="flex flex-col items-center pt-0.5">
        <div className={`w-2 h-2 rounded-full ${stateStyle.dot}`} />
      </div>
      <Icon className="w-3 h-3 mt-1 text-[var(--text-tertiary)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className={`text-[12px] ${stateStyle.text} truncate`}>
          <span className="text-[10px] uppercase tracking-wider mr-1.5 text-[var(--text-tertiary)]">#{idx + 1} {step.type}</span>
          {stepLabel}
        </div>
        {result?.executed_at && (
          <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{fmtDate(result.executed_at)} · {stateStyle.label}</div>
        )}
        {state === 'error' && result?.error && (
          <div className="text-[10px] text-red-600 mt-0.5 flex items-start gap-1">
            <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{result.error}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ExecutionCard({ exec, busy, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS_STYLE[exec.status] || STATUS_STYLE.completed;
  const isActive = exec.status === 'running' || exec.status === 'paused';
  const op = busy[exec.id];
  const steps = Array.isArray(exec.workflow_steps) ? exec.workflow_steps : [];
  const results = Array.isArray(exec.step_results) ? exec.step_results : [];
  const doneCount = results.filter((r) => r && r.success !== false).length;
  const totalSteps = steps.length;

  return (
    <div className="border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-3 hover:bg-[var(--bg-inset)] rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2 mb-1">
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
          )}
          <Zap className="w-3.5 h-3.5 text-[#338edc]" />
          <span className="text-[13px] font-medium text-[var(--text-primary)]">{exec.workflow_name || `Workflow #${exec.workflow_id}`}</span>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.cls}`}>{st.label}</span>
        </div>
        <div className="text-[11px] text-[var(--text-tertiary)] ml-5 flex items-center gap-2 flex-wrap">
          <span>Gestartet: {fmtDate(exec.started_at)}</span>
          {exec.started_by_name && <span>· von {exec.started_by_name}</span>}
          {totalSteps > 0 && (
            <span>· {doneCount}/{totalSteps} Schritte</span>
          )}
        </div>
        {totalSteps > 0 && (
          <div className="ml-5 mt-2 h-1 bg-[var(--bg-inset)] rounded-full overflow-hidden">
            <div
              className={`h-full ${exec.status === 'failed' || exec.status === 'cancelled' ? 'bg-red-400' : exec.status === 'completed' ? 'bg-emerald-500' : 'bg-[#338edc]'} transition-all`}
              style={{ width: `${Math.min(100, Math.round((doneCount / totalSteps) * 100))}%` }}
            />
          </div>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 ml-5 border-t border-[var(--border-card)] bg-[var(--bg-inset)] rounded-b-lg">
          {steps.length === 0 ? (
            <div className="text-[11px] text-[var(--text-tertiary)] italic py-2">Keine Schritte definiert.</div>
          ) : (
            <div className="divide-y divide-[var(--border-card)]">
              {steps.map((s, i) => <StepRow key={s.id || i} step={s} idx={i} exec={exec} />)}
            </div>
          )}
          {exec.error_message && (
            <div className="mt-2 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <span>{exec.error_message}</span>
            </div>
          )}
          {isActive && (
            <div className="flex items-center gap-2 mt-3">
              {exec.status === 'paused' ? (
                <button onClick={() => onAction(exec.id, 'resume')} disabled={!!op} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 text-emerald-700 text-[11px] hover:bg-emerald-100 disabled:opacity-50">
                  {op === 'resume' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Fortsetzen
                </button>
              ) : (
                <button onClick={() => onAction(exec.id, 'pause')} disabled={!!op} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-50 text-amber-700 text-[11px] hover:bg-amber-100 disabled:opacity-50">
                  {op === 'pause' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pause className="w-3 h-3" />} Pausieren
                </button>
              )}
              <button onClick={() => onAction(exec.id, 'stop')} disabled={!!op} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700 text-[11px] hover:bg-red-100 disabled:opacity-50">
                {op === 'stop' ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} Beenden
              </button>
            </div>
          )}
          {exec.status === 'completed' && (
            <div className="text-[11px] text-emerald-600 inline-flex items-center gap-1 mt-2">
              <CheckCircle2 className="w-3 h-3" /> Abgeschlossen {exec.completed_at && `· ${fmtDate(exec.completed_at)}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkflowTab({ leadId, executions = [], workflows = [], onChanged }) {
  const [busy, setBusy] = useState({});
  const [starting, setStarting] = useState(false);
  const [picker, setPicker] = useState(false);

  const action = async (id, op) => {
    setBusy((p) => ({ ...p, [id]: op }));
    try {
      await api(`/api/sales/workflow-executions/${id}/${op}`, { method: 'POST' });
      onChanged?.();
    } catch (e) {
      alert(`${op} fehlgeschlagen: ${e.message}`);
    } finally {
      setBusy((p) => ({ ...p, [id]: null }));
    }
  };

  const startWorkflow = async (wfId) => {
    setStarting(true);
    try {
      await api(`/api/sales/workflow-builder/${wfId}/run`, { method: 'POST', body: { lead_id: leadId } });
      setPicker(false);
      onChanged?.();
    } catch (e) {
      alert('Workflow konnte nicht gestartet werden: ' + e.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[13px] text-[var(--text-tertiary)]">{executions.length} Workflow-Durchläufe</span>
        <button
          onClick={() => setPicker((v) => !v)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#338edc] text-white text-[12px] hover:opacity-90"
        >
          <Zap className="w-3 h-3" /> Workflow starten
        </button>
      </div>

      {picker && (
        <div className="border border-[var(--border-card)] rounded-lg bg-[var(--bg-card)] p-3">
          <div className="text-[12px] text-[var(--text-tertiary)] mb-2">Verfügbare Workflows</div>
          {workflows.length === 0 ? (
            <div className="text-[13px] text-[var(--text-tertiary)] italic">Keine aktiven Workflows verfügbar.</div>
          ) : (
            <div className="space-y-1">
              {workflows.filter((w) => w.is_active).map((w) => (
                <button
                  key={w.id}
                  onClick={() => startWorkflow(w.id)}
                  disabled={starting}
                  className="w-full text-left px-3 py-2 rounded hover:bg-[var(--bg-inset)] text-[13px] text-[var(--text-primary)] flex items-center gap-2 disabled:opacity-50"
                >
                  <Zap className="w-3.5 h-3.5 text-[#338edc]" />
                  <span className="font-medium">{w.name}</span>
                  {w.description && <span className="text-[11px] text-[var(--text-tertiary)] truncate">— {w.description}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {executions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[var(--text-tertiary)]">
          <Zap className="w-8 h-8 mb-2 opacity-50" />
          <span className="text-[13px]">Noch kein Workflow gestartet</span>
        </div>
      ) : (
        <div className="space-y-2">
          {executions.map((e) => (
            <ExecutionCard key={e.id} exec={e} busy={busy} onAction={action} />
          ))}
        </div>
      )}
    </div>
  );
}
